import React from 'react';

export interface BlockWireframeThumbnailProps {
  type: string;
  className?: string;
}

export function BlockWireframeThumbnail({
  type,
  className = 'w-full h-9',
}: BlockWireframeThumbnailProps): React.JSX.Element {
  const lower = type.toLowerCase();

  // Columns & Grid
  if (lower.includes('grid2col') || lower.includes('2col') || lower.includes('columns2')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <rect x="26" y="2" width="20" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
      </svg>
    );
  }

  if (lower.includes('grid3col') || lower.includes('3col') || lower.includes('columns3')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="12" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <rect x="18" y="2" width="12" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <rect x="34" y="2" width="12" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
      </svg>
    );
  }

  // Section / Container
  if (lower.includes('container') || lower.includes('section')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="44" height="28" rx="2" className="fill-muted/40 stroke-border" strokeWidth="1.5" strokeDasharray="3 3" />
        <line x1="8" y1="10" x2="24" y2="10" className="stroke-foreground/40" strokeWidth="2" strokeLinecap="round" />
        <line x1="8" y1="16" x2="38" y2="16" className="stroke-foreground/20" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="8" y1="22" x2="30" y2="22" className="stroke-foreground/20" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }

  // Hero Split / Hero Video
  if (lower.includes('hero')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="44" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <line x1="6" y1="9" x2="22" y2="9" className="stroke-[#3b97e3]" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="6" y1="15" x2="20" y2="15" className="stroke-foreground/30" strokeWidth="1.5" strokeLinecap="round" />
        <rect x="6" y="20" width="10" height="5" rx="1.5" className="fill-[#3b97e3]" />
        <rect x="26" y="6" width="16" height="20" rx="1.5" className="fill-muted-foreground/20 stroke-border" strokeWidth="1" />
      </svg>
    );
  }

  // Pricing Table / Comparison
  if (lower.includes('pricing') || lower.includes('plan') || lower.includes('fee')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="4" width="18" height="24" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <rect x="26" y="2" width="18" height="28" rx="2" className="fill-muted stroke-[#3b97e3]" strokeWidth="1.5" />
        <line x1="8" y1="9" x2="18" y2="9" className="stroke-foreground/40" strokeWidth="2" strokeLinecap="round" />
        <line x1="30" y1="8" x2="40" y2="8" className="stroke-[#3b97e3]" strokeWidth="2" strokeLinecap="round" />
        <rect x="8" y="22" width="10" height="3" rx="1" className="fill-foreground/20" />
        <rect x="30" y="23" width="10" height="4" rx="1" className="fill-[#3b97e3]" />
      </svg>
    );
  }

  // Testimonials / Reviews
  if (lower.includes('testimonial') || lower.includes('review') || lower.includes('trust')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="44" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <circle cx="10" cy="11" r="4" className="fill-[#3b97e3]" />
        <line x1="18" y1="9" x2="34" y2="9" className="stroke-foreground/50" strokeWidth="2" strokeLinecap="round" />
        <line x1="18" y1="13" x2="26" y2="13" className="stroke-foreground/30" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="6" y1="20" x2="42" y2="20" className="stroke-foreground/20" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="6" y1="24" x2="30" y2="24" className="stroke-foreground/20" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }

  // Faculty / Educator / Roster
  if (lower.includes('educator') || lower.includes('faculty') || lower.includes('teacher')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="19" height="26" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <circle cx="12.5" cy="11" r="4" className="fill-muted-foreground/30" />
        <line x1="7" y1="19" x2="18" y2="19" className="stroke-foreground/50" strokeWidth="1.5" strokeLinecap="round" />
        <rect x="26" y="3" width="19" height="26" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <circle cx="35.5" cy="11" r="4" className="fill-muted-foreground/30" />
        <line x1="30" y1="19" x2="41" y2="19" className="stroke-foreground/50" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }

  // Live ERP Courses / Storefront
  if (lower.includes('course') || lower.includes('store') || lower.includes('catalog')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="44" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <rect x="2" y="2" width="44" height="12" rx="2" className="fill-muted-foreground/20" />
        <line x1="6" y1="18" x2="26" y2="18" className="stroke-foreground/60" strokeWidth="2" strokeLinecap="round" />
        <line x1="6" y1="23" x2="18" y2="23" className="stroke-[#3b97e3]" strokeWidth="2" strokeLinecap="round" />
        <rect x="34" y="21" width="8" height="5" rx="1" className="fill-[#3b97e3]" />
      </svg>
    );
  }

  // Timetable / Schedule / Calendar
  if (lower.includes('timetable') || lower.includes('sched')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="44" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <line x1="2" y1="10" x2="46" y2="10" className="stroke-border" strokeWidth="1" />
        <line x1="16" y1="2" x2="16" y2="30" className="stroke-border" strokeWidth="1" />
        <line x1="32" y1="2" x2="32" y2="30" className="stroke-border" strokeWidth="1" />
        <rect x="18" y="13" width="12" height="6" rx="1" className="fill-[#3b97e3]/40" />
        <rect x="4" y="21" width="10" height="6" rx="1" className="fill-emerald-500/40" />
      </svg>
    );
  }

  // Form / Lead / Checkout
  if (lower.includes('form') || lower.includes('lead') || lower.includes('checkout') || lower.includes('callback')) {
    return (
      <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="2" width="40" height="28" rx="2" className="fill-muted stroke-border" strokeWidth="1.5" />
        <rect x="8" y="7" width="32" height="4" rx="1" className="fill-background stroke-border" strokeWidth="1" />
        <rect x="8" y="14" width="32" height="4" rx="1" className="fill-background stroke-border" strokeWidth="1" />
        <rect x="8" y="21" width="20" height="5" rx="1.5" className="fill-[#3b97e3]" />
      </svg>
    );
  }

  // Default Atomic / Text / RichText
  return (
    <svg className={className} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="44" height="28" rx="2" className="fill-muted/60 stroke-border" strokeWidth="1.5" />
      <line x1="8" y1="9" x2="28" y2="9" className="stroke-[#3b97e3]" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="8" y1="16" x2="40" y2="16" className="stroke-foreground/30" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="8" y1="22" x2="34" y2="22" className="stroke-foreground/20" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
